import * as turf from "@turf/turf"

import { getPreviousSegmentsTotalLength } from "@l2r-front/l2r-geodata"

import {
    getSegmentCircleIntersection,
    computeBrushRadius,
    extractCoordinatesBetweenDistances,
} from "./geometryUtils"
import {
    getRoadLinearLocation,
    isLinearLocationInverted,
    convertParametricsToLinearLocation,
    convertLinearLocationToParametrics,
    convertLinearLocationToRelativePositions,
} from "./linearLocationUtils"
import {
    mergeOverlappingRanges,
    subtractRangeFromRanges,
    splitLinearLocationByOverlaps,
} from "./rangeUtils"

// Re-export for backward compatibility
export { getSegmentCircleIntersection as segmentInsideCircle }

export function initStateFromFeatures(state, features) {
    features.forEach(feature => {
        // Extract brushProperties from feature properties using keys defined in state.options.brush.properties
        let brushProperties = null
        const brushPropertiesKeys = state.options?.brush?.properties ? Object.keys(state.options.brush.properties) : []
        if (brushPropertiesKeys.length > 0) {
            brushProperties = {}
            for (const key of brushPropertiesKeys) {
                if (feature.properties[key] !== undefined) {
                    brushProperties[key] = feature.properties[key]
                }
            }
            if (Object.keys(brushProperties).length === 0) {
                brushProperties = null
            }
        }

        state.parametricSegments.push({
            featureIndex: feature.properties.featureIndex,
            segmentIndex: feature.properties.segmentIndex,
            parametrics: [[feature.properties.start, feature.properties.end]],
            inverted: feature.properties.inverted,
            properties: feature.properties,
            brushProperties,
        })
        state.featureIds.push(feature.id)
    })
}

export function addParametrics(parametricsToAdd, currentParametrics) {
    parametricsToAdd.forEach(param => {
        param.parametrics = [param.parametrics]
        currentParametrics.push(param)
    })
    return currentParametrics
}

function areBrushPropertiesEqual(a, b) {
    if (a === null && b === null) {
        return true
    }
    if (a === null || b === null) {
        return false
    }
    return JSON.stringify(a) === JSON.stringify(b)
}

function getBrushPropertiesKey(brushProperties) {
    if (brushProperties === null || brushProperties === undefined) {
        return "null"
    }
    return JSON.stringify(brushProperties)
}

function expandMergedParametricsToSegments(mergedParametrics) {
    const segmentsMap = new Map()

    for (const param of mergedParametrics) {
        const featureIndex = param.featureIndex
        const inverted = param.inverted ?? param.properties?.inverted
        const brushProperties = param.brushProperties ?? param.properties?.brushProperties ?? null
        const brushKey = getBrushPropertiesKey(brushProperties)

        if (param.properties?.mergedStart && param.properties?.mergedEnd) {
            const { mergedStart, mergedEnd } = param.properties

            for (let segIdx = mergedStart.segmentIndex; segIdx <= mergedEnd.segmentIndex; segIdx++) {
                const key = `${featureIndex}-${segIdx}-${brushKey}`

                if (!segmentsMap.has(key)) {
                    segmentsMap.set(key, { featureIndex, segmentIndex: segIdx, inverted, brushProperties, parametrics: [] })
                }

                const segment = segmentsMap.get(key)
                const rangeStart = segIdx === mergedStart.segmentIndex ? mergedStart.start : 0
                const rangeEnd = segIdx === mergedEnd.segmentIndex ? mergedEnd.end : 1

                if (rangeStart !== rangeEnd) {
                    segment.parametrics.push([rangeStart, rangeEnd])
                }
            }
        } else {
            const segmentIndex = param.segmentIndex
            const key = `${featureIndex}-${segmentIndex}-${brushKey}`

            if (!segmentsMap.has(key)) {
                segmentsMap.set(key, { featureIndex, segmentIndex, inverted, brushProperties, parametrics: [] })
            }

            const segment = segmentsMap.get(key)
            if (Array.isArray(param.parametrics)) {
                for (const p of param.parametrics) {
                    if (Array.isArray(p) && p[0] !== p[1]) {
                        segment.parametrics.push([p[0], p[1]])
                    }
                }
            }
        }
    }

    for (const segment of segmentsMap.values()) {
        segment.parametrics = mergeOverlappingRanges(segment.parametrics)
    }

    return segmentsMap
}

export function substractParametrics(parametricsToSubtract, currentParametrics) {
    const segmentsMap = expandMergedParametricsToSegments(currentParametrics)

    parametricsToSubtract.forEach(param => {
        // Iterate over all segments to find matching featureIndex and segmentIndex
        // (regardless of brushProperties)
        for (const [key, segment] of segmentsMap.entries()) {
            if (segment.featureIndex === param.featureIndex && segment.segmentIndex === param.segmentIndex) {
                segment.parametrics = subtractRangeFromRanges(segment.parametrics, param.parametrics)
                if (!segment.parametrics.length) {
                    segmentsMap.delete(key)
                }
            }
        }
    })

    const result = []
    for (const segment of segmentsMap.values()) {
        if (segment.parametrics.length > 0) {
            result.push({
                featureIndex: segment.featureIndex,
                segmentIndex: segment.segmentIndex,
                inverted: segment.inverted,
                parametrics: segment.parametrics,
                brushProperties: segment.brushProperties,
            })
        }
    }

    return result
}

function mergeConsecutiveParametrics(segmentsParametrics) {
    if (!segmentsParametrics.length) {
        return segmentsParametrics
    }

    // Group by segment and brushProperties, then collect all ranges
    const groupedBySegment = new Map()

    for (const param of segmentsParametrics) {
        const featureIndex = param.featureIndex
        const inverted = param.inverted ?? param.properties?.inverted
        const brushProperties = param.brushProperties ?? param.properties?.brushProperties ?? null
        const brushKey = getBrushPropertiesKey(brushProperties)

        if (param.properties?.mergedStart && param.properties?.mergedEnd) {
            const { mergedStart, mergedEnd } = param.properties

            for (let segIdx = mergedStart.segmentIndex; segIdx <= mergedEnd.segmentIndex; segIdx++) {
                const key = `${featureIndex}-${segIdx}-${brushKey}`
                if (!groupedBySegment.has(key)) {
                    groupedBySegment.set(key, { featureIndex, segmentIndex: segIdx, inverted, brushProperties, ranges: [] })
                }

                const group = groupedBySegment.get(key)
                const rangeStart = segIdx === mergedStart.segmentIndex ? mergedStart.start : 0
                const rangeEnd = segIdx === mergedEnd.segmentIndex ? mergedEnd.end : 1

                if (rangeStart !== rangeEnd) {
                    group.ranges.push([rangeStart, rangeEnd])
                }
            }
        } else {
            const segmentIndex = param.segmentIndex
            const key = `${featureIndex}-${segmentIndex}-${brushKey}`

            if (!groupedBySegment.has(key)) {
                groupedBySegment.set(key, { featureIndex, segmentIndex, inverted, brushProperties, ranges: [] })
            }

            const group = groupedBySegment.get(key)
            for (const p of param.parametrics) {
                if (p[0] !== p[1]) {
                    group.ranges.push([p[0], p[1]])
                }
            }
        }
    }

    // Merge overlapping ranges and create consolidated segments
    const consolidatedSegments = []
    for (const group of groupedBySegment.values()) {
        const mergedRanges = mergeOverlappingRanges(group.ranges)
        for (const range of mergedRanges) {
            if (range[0] !== range[1]) {
                consolidatedSegments.push({
                    featureIndex: group.featureIndex,
                    segmentIndex: group.segmentIndex,
                    inverted: group.inverted,
                    brushProperties: group.brushProperties,
                    start: range[0],
                    end: range[1],
                })
            }
        }
    }

    // Sort by featureIndex, segmentIndex, then start
    consolidatedSegments.sort((a, b) => {
        if (a.featureIndex !== b.featureIndex) {
            return a.featureIndex - b.featureIndex
        }
        if (a.segmentIndex !== b.segmentIndex) {
            return a.segmentIndex - b.segmentIndex
        }
        return a.start - b.start
    })

    // Merge consecutive segments that touch (end=1 -> start=0) only if they have the same brushProperties
    const merged = []
    let currentMerge = null

    for (const seg of consolidatedSegments) {
        const isSegmentStart = seg.start === 0
        const isSegmentEnd = seg.end === 1

        const canContinueMerge =
            currentMerge &&
            seg.featureIndex === currentMerge.featureIndex &&
            seg.segmentIndex === currentMerge.lastSegmentIndex + 1 &&
            currentMerge.lastWasFullEnd &&
            isSegmentStart &&
            areBrushPropertiesEqual(seg.brushProperties, currentMerge.brushProperties)

        if (canContinueMerge) {
            currentMerge.lastSegmentIndex = seg.segmentIndex
            currentMerge.lastWasFullEnd = isSegmentEnd
            currentMerge.result.properties.mergedEnd = { end: seg.end, segmentIndex: seg.segmentIndex }
        } else {
            if (currentMerge) {
                merged.push(currentMerge.result)
            }
            currentMerge = {
                featureIndex: seg.featureIndex,
                lastSegmentIndex: seg.segmentIndex,
                lastWasFullEnd: isSegmentEnd,
                brushProperties: seg.brushProperties,
                result: {
                    featureIndex: seg.featureIndex,
                    segmentIndex: seg.segmentIndex,
                    parametrics: [[seg.start, seg.end]],
                    inverted: seg.inverted,
                    brushProperties: seg.brushProperties,
                    properties: {
                        featureIndex: seg.featureIndex,
                        mergedStart: { start: seg.start, segmentIndex: seg.segmentIndex },
                        mergedEnd: { end: seg.end, segmentIndex: seg.segmentIndex },
                        inverted: seg.inverted,
                        brushProperties: seg.brushProperties,
                    },
                },
            }
        }
    }

    if (currentMerge) {
        merged.push(currentMerge.result)
    }

    return merged
}

function createFeatureKey(props) {
    const { mergedStart, mergedEnd, featureIndex, brushProperties } = props
    const brushKey = getBrushPropertiesKey(brushProperties)
    return `${featureIndex}-${mergedStart?.segmentIndex ?? ""}-${mergedStart?.start ?? ""}-${mergedEnd?.segmentIndex ?? ""}-${mergedEnd?.end ?? ""}-${brushKey}`
}

export function createParametrics(mode, state, paintedParametrics, addition = true, existingFeatures = []) {
    let featuresUpdated = false

    let newParametrics
    if (addition) {
        // In addition mode, first subtract the painted areas from existing segments
        // so that the new painted segments overwrite the old ones in overlapping areas
        const clearedParametrics = substractParametrics(paintedParametrics, state.parametricSegments)
        // Then add the new painted segments
        newParametrics = addParametrics(paintedParametrics, clearedParametrics)
    } else {
        newParametrics = substractParametrics(paintedParametrics, state.parametricSegments)
    }

    const mergedParametrics = mergeConsecutiveParametrics(newParametrics || [])
    state.parametricSegments = mergedParametrics

    // Build lookup map of existing draw features
    const existingDrawFeaturesMap = new Map()
    for (const featureId of state.featureIds) {
        const feature = mode.getFeature(featureId)
        if (feature) {
            existingDrawFeaturesMap.set(createFeatureKey(feature.properties), featureId)
        }
    }

    const parametricsToCreate = []
    const featuresToKeep = new Set()

    for (const parametric of mergedParametrics) {
        const key = createFeatureKey(parametric.properties)
        const matchingFeatureId = existingDrawFeaturesMap.get(key)

        if (matchingFeatureId) {
            featuresToKeep.add(matchingFeatureId)
        } else {
            parametricsToCreate.push(parametric)
        }
    }

    // Delete features that are no longer needed
    for (const featureId of state.featureIds) {
        if (!featuresToKeep.has(featureId)) {
            mode.deleteFeature(featureId)
            featuresUpdated = true
        }
    }
    state.featureIds = [...featuresToKeep]

    // Create new features
    for (const parametric of parametricsToCreate) {
        const roadFeature = state.options.roadSegments.features[parametric.featureIndex]
        const { mergedStart, mergedEnd } = parametric.properties

        const linearLocation = convertParametricsToLinearLocation(roadFeature, mergedStart, mergedEnd)
        const splitSegments = splitLinearLocationByOverlaps(linearLocation, existingFeatures)

        for (const splitSegment of splitSegments) {
            const segmentLinearLocation = splitSegment.linearLocation
            const existingProperties = splitSegment.existingFeatureProperties

            const positions = convertLinearLocationToRelativePositions(roadFeature, segmentLinearLocation)
            if (!positions) {
                continue
            }

            const { startDistance, endDistance } = positions
            const coordinates = extractCoordinatesBetweenDistances(roadFeature, startDistance, endDistance)

            // Compute new parametrics for this segment
            const segmentParametrics = convertLinearLocationToParametrics(roadFeature, segmentLinearLocation)
            const segmentMergedStart = segmentParametrics?.mergedStart || mergedStart
            const segmentMergedEnd = segmentParametrics?.mergedEnd || mergedEnd

            const featureProperties = {
                featureIndex: parametric.featureIndex,
                mergedStart: segmentMergedStart,
                mergedEnd: segmentMergedEnd,
                linearLocation: [segmentLinearLocation],
                ...(parametric.brushProperties || {}),
            }

            // Copy properties from overlapping existing feature
            if (existingProperties) {
                const { linearLocation: _existingLinearLoc, brushProperties: _existingBrushProps, ...otherProperties } = existingProperties
                Object.assign(featureProperties, otherProperties)
                featureProperties.linearLocation = [segmentLinearLocation]
                Object.assign(featureProperties, parametric.brushProperties || {})
            }

            const geoJSONFeature = turf.lineString(coordinates, featureProperties)
            const drawFeature = mode.newFeature(geoJSONFeature)
            mode.addFeature(drawFeature)
            state.featureIds.push(drawFeature.id)
            featuresUpdated = true
        }
    }

    return featuresUpdated
}

export function initStateFromStartingFeatures(state) {
    state.parametricSegments = []
    state.featureIds = []

    if (!state.options.roadSegments) {
        return []
    }

    const paintedParametrics = []

    turf.segmentEach(state.options.roadSegments, (currentSegment, featureIndex, _multiFeatureIndex, _geometryIndex, segmentIndex) => {
        const roadFeature = state.options.roadSegments.features[featureIndex]
        const roadLength = turf.length(roadFeature)
        const segmentRelativeStart = getPreviousSegmentsTotalLength(roadFeature, segmentIndex) / roadLength
        const segmentRelativeEnd = segmentRelativeStart + turf.length(currentSegment) / roadLength

        const roadLinearLocation = getRoadLinearLocation(roadFeature)
        const inverted = isLinearLocationInverted(roadLinearLocation)

        const linearRange = Math.abs(roadLinearLocation.end - roadLinearLocation.start)
        const currentSegmentLinearLocation = !inverted
            ? {
                start: roadLinearLocation.start + segmentRelativeStart * linearRange,
                end: roadLinearLocation.start + segmentRelativeEnd * linearRange,
            }
            : {
                start: roadLinearLocation.start - segmentRelativeStart * linearRange,
                end: roadLinearLocation.start - segmentRelativeEnd * linearRange,
            }

        const startingFeaturesLinearLocations = state.startingFeatures?.linear_location
            ? state.startingFeatures.linear_location
            : state.startingFeatures
                ? state.startingFeatures.features
                    .filter(f => f.properties["side"] === roadFeature.properties["side"])
                    .map(f => f.properties["linearLocation"])
                    .flat()
                : []

        for (const startingFeatureLinearLocation of startingFeaturesLinearLocations) {
            const minLinearRange = !inverted
                ? (startingFeatureLinearLocation.start < currentSegmentLinearLocation.start ? startingFeatureLinearLocation : currentSegmentLinearLocation)
                : (startingFeatureLinearLocation.end < currentSegmentLinearLocation.end ? startingFeatureLinearLocation : currentSegmentLinearLocation)
            const maxLinearRange = minLinearRange === startingFeatureLinearLocation ? currentSegmentLinearLocation : startingFeatureLinearLocation

            const doIntersect = !inverted
                ? minLinearRange.end >= maxLinearRange.start
                : minLinearRange.start >= maxLinearRange.end

            if (doIntersect) {
                const linearIntersection = {
                    start: !inverted ? maxLinearRange.start : Math.min(minLinearRange.start, maxLinearRange.start),
                    end: !inverted ? Math.min(minLinearRange.end, maxLinearRange.end) : maxLinearRange.end,
                }

                const segmentRange = Math.abs(currentSegmentLinearLocation.end - currentSegmentLinearLocation.start)
                const intersectionParametrics = !inverted
                    ? [
                        (linearIntersection.start - currentSegmentLinearLocation.start) / segmentRange,
                        (linearIntersection.end - currentSegmentLinearLocation.start) / segmentRange,
                    ]
                    : [
                        (currentSegmentLinearLocation.start - linearIntersection.start) / segmentRange,
                        (currentSegmentLinearLocation.start - linearIntersection.end) / segmentRange,
                    ]

                paintedParametrics.push({
                    featureIndex,
                    multiFeatureIndex: _multiFeatureIndex,
                    geometryIndex: _geometryIndex,
                    segmentIndex,
                    parametrics: intersectionParametrics,
                    inverted,
                    brushProperties: state.options?.brush ? { name: state.options.brush.name } : null,
                })
            }
        }
    })

    return paintedParametrics
}

export function computeBrushRadiusAtMousePosition(mode, mouseEvent) {
    return computeBrushRadius(mode.map, mouseEvent)
}

export function computeBrushParametrics(state, brushRadius, mouseEvent, brushProperties = {}) {
    const paintedParametrics = []
    const brushCenter = [mouseEvent.lngLat.lng, mouseEvent.lngLat.lat]

    turf.segmentEach(state.roadSegments, (currentSegment, featureIndex, multiFeatureIndex, geometryIndex, segmentIndex) => {
        const segmentCoords = currentSegment.geometry.coordinates

        const intersectedParametrics = getSegmentCircleIntersection(
            segmentCoords[0],
            segmentCoords[1],
            brushCenter,
            brushRadius,
        )

        if (intersectedParametrics.length) {
            const segmentLinearLocation = currentSegment.properties.linearLocation?.[0]
            paintedParametrics.push({
                featureIndex,
                multiFeatureIndex,
                geometryIndex,
                segmentIndex,
                parametrics: intersectedParametrics,
                inverted: segmentLinearLocation ? segmentLinearLocation.start > segmentLinearLocation.end : false,
                brushProperties,
            })
        }
    })

    return paintedParametrics
}
